import React from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  Divider,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

// Icons
import DoneIcon from '@mui/icons-material/Done';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArticleIcon from '@mui/icons-material/Article';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import EventIcon from '@mui/icons-material/Event';
import NotificationsIcon from '@mui/icons-material/Notifications';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const NotificationItem = ({ notification, onMarkAsRead, onViewDetails }) => {
  // Notification type info mapping
  const notificationTypeInfo = {
    priceAlert: {
      title: 'Price Alert',
      icon: <TrendingUpIcon fontSize="small" />,
      color: '#4CAF50' // green
    },
    newsAlert: {
      title: 'News Alert',
      icon: <ArticleIcon fontSize="small" />,
      color: '#2196F3' // blue
    },
    congressTrade: {
      title: 'Congressional Trading Alert',
      icon: <AccountBalanceIcon fontSize="small" />,
      color: '#9C27B0' // purple
    },
    insiderTrade: {
      title: 'Insider Trading Alert',
      icon: <BusinessIcon fontSize="small" />,
      color: '#FF9800' // orange
    },
    marketEvent: {
      title: 'Market Event Alert',
      icon: <EventIcon fontSize="small" />,
      color: '#F44336' // red
    }
  };

  // Get notification type info
  const typeInfo = notificationTypeInfo[notification.type] || {
    title: 'Notification',
    icon: <NotificationsIcon fontSize="small" />,
    color: '#757575' // gray
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);

    // If today, show time
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit'
      });
    }

    // If yesterday, show "Yesterday"
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }

    // Otherwise show date
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric'
    });
  };

  // Get delivery info
  const getDeliveryInfo = () => {
    if (!notification.deliveries || notification.deliveries.length === 0) {
      return null;
    }

    const channels = notification.deliveries
      .filter(d => d.status === 'delivered' || d.status === 'sent')
      .map(d => d.channel);

    if (channels.length === 0) return null;

    const channelNames = {
      site: 'Site',
      email: 'Email',
      telegram: 'Telegram',
      discord: 'Discord',
      whatsapp: 'WhatsApp'
    };

    return `Sent via ${channels.map(c => channelNames[c] || c).join(', ')}`;
  };

  // Handle click on the notification
  const handleNotificationClick = () => {
    // Mark as read if not already
    if (!notification.isRead) {
      onMarkAsRead(notification._id);
    }
    // Open details modal
    onViewDetails(notification);
  };

  // Get metadata display based on notification type
  const getMetadataDisplay = () => {
    const { metadata, type } = notification;
    if (!metadata) return null;

    switch (type) {
      case 'priceAlert':
        return (
          <>
            {metadata.changePercent !== undefined && (
              <Chip
                label={`${metadata.changePercent >= 0 ? '+' : ''}${metadata.changePercent.toFixed(2)}%`}
                size="small"
                color={metadata.changePercent >= 0 ? 'success' : 'error'}
                variant="outlined"
                sx={{ mr: 1 }}
              />
            )}
            {metadata.price && (
              <Chip
                label={`$${typeof metadata.price === 'number' ? metadata.price.toFixed(2) : metadata.price}`}
                size="small"
                variant="outlined"
              />
            )}
          </>
        );

      case 'newsAlert':
        return (
          <>
            {metadata.source && (
              <Chip
                label={metadata.source}
                size="small"
                variant="outlined"
                sx={{ mr: 1 }}
              />
            )}
            {metadata.url && (
              <Tooltip title="Open article">
                <IconButton
                  size="small"
                  component="a"
                  href={metadata.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </>
        );

      case 'congressTrade':
        return (
          <>
            {metadata.symbol && (
              <Chip
                label={metadata.symbol}
                size="small"
                variant="outlined"
                sx={{ mr: 1 }}
              />
            )}
            {metadata.tradeType && (
              <Chip
                label={metadata.tradeType.toUpperCase()}
                size="small"
                color={metadata.tradeType === 'buy' ? 'success' : 'error'}
                variant="outlined"
                sx={{ mr: 1 }}
              />
            )}
            {metadata.amount && (
              <Chip
                label={`$${typeof metadata.amount === 'number' ? metadata.amount.toLocaleString() : metadata.amount}`}
                size="small"
                variant="outlined"
              />
            )}
          </>
        );

      case 'insiderTrade':
        return (
          <>
            {metadata.symbol && (
              <Chip
                label={metadata.symbol}
                size="small"
                variant="outlined"
                sx={{ mr: 1 }}
              />
            )}
            {metadata.transactionType && (
              <Chip
                label={metadata.transactionType}
                size="small"
                color={metadata.transactionType === 'Purchase' ? 'success' : 'error'}
                variant="outlined"
                sx={{ mr: 1 }}
              />
            )}
            {metadata.value && (
              <Chip
                label={`$${typeof metadata.value === 'number' ? Math.abs(metadata.value).toLocaleString() : metadata.value}`}
                size="small"
                variant="outlined"
              />
            )}
          </>
        );

      case 'marketEvent':
        return (
          <>
            {metadata.type && (
              <Chip
                label={metadata.type}
                size="small"
                variant="outlined"
                sx={{ mr: 1 }}
              />
            )}
            {metadata.symbol && (
              <Chip
                label={metadata.symbol}
                size="small"
                variant="outlined"
              />
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        position: 'relative',
        cursor: 'pointer',
        borderLeft: `4px solid ${typeInfo.color}`,
        bgcolor: !notification.isRead ? alpha(typeInfo.color, 0.05) : 'transparent',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 2
        }
      }}
      onClick={handleNotificationClick}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              mr: 1.5,
              color: typeInfo.color,
              display: 'flex'
            }}
          >
            {typeInfo.icon}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {typeInfo.title}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            {formatDate(notification.createdAt)}
          </Typography>

          {!notification.isRead && (
            <Tooltip title="Mark as read">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkAsRead(notification._id);
                }}
              >
                <DoneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
        {notification.title}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {notification.content}
      </Typography>

      {getMetadataDisplay() && (
        <>
          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            {getMetadataDisplay()}
          </Box>
        </>
      )}

      {getDeliveryInfo() && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {getDeliveryInfo()}
        </Typography>
      )}
    </Paper>
  );
};

export default NotificationItem;